import { Nav } from '@/components/Nav'
import { StickyCta } from '@/components/StickyCta'
import { Footer } from '@/components/Footer'
import { Seo } from '@/components/Seo'
import { SeoLocale } from '@/components/SeoLocale'
import { Analytics } from '@/components/Analytics'
import { ScrollToHash } from '@/components/ScrollToHash'
import { ClientLogos } from '@/components/ClientLogos'
import { Hero } from '@/sections/Hero/Hero'
import { DnaLazy } from '@/sections/DnaField/DnaLazy'
import { TwoTracks } from '@/sections/TwoTracks/TwoTracks'
import { ValueCalculator } from '@/sections/ValueCalculator/ValueCalculator'
import { Partners } from '@/sections/Partners/Partners'
import { ReadNext } from '@/sections/ReadNext/ReadNext'
/**
 * Кейсы и поток диагностики раньше грузились ленивыми чанками: они тянули
 * framer-motion, а он вместе с ними лежал в entry-бандле — на эмулированном
 * мобильном CPU выполнение entry занимало 2,5 секунды, блокировка главного
 * потока 530 мс, мобильный Lighthouse 77.
 *
 * Разбиение чинило симптом и ломало главное. React.lazy при гидратации
 * подставляет заглушку Suspense вместо уже отрендеренного на сервере
 * поддерева — и на медленной сети блок кейсов ИСЧЕЗАЛ со страницы:
 * воспроизведено задержкой чанков в 400 мс, текст /en худел с 37 184 до
 * 30 704 символов, якорь #cases пропадал (CI это и поймал).
 *
 * Вылечена причина: framer-motion выкинут целиком (четыре вызова заменены
 * на IntersectionObserver, rAF и CSS-переходы), обе секции снова
 * статические. Бандл меньше, чем был с ленивой загрузкой, а SSR-контент
 * больше нечему выбрасывать.
 */
import { Cases } from '@/sections/Cases/Cases'
import { Team } from '@/sections/Team/Team'
import { Faq } from '@/sections/Faq/Faq'
import { FinalCta } from '@/sections/FinalCta/FinalCta'
import { useScrollDepth } from '@/lib/useScrollDepth'
import { FontPreload } from '@/components/FontPreload'
import { Audience } from '@/sections/Audience/Audience'
import { HowItWorks } from '@/sections/HowItWorks/HowItWorks'
import { WhyNotDiy } from '@/sections/WhyNotDiy/WhyNotDiy'

export default function App() {
  // Глубина прокрутки 25/50/75/100 — единая точка инициализации,
  // чтобы хук не дублировался по разным секциям.
  useScrollDepth()

  return (
    <>
      <Seo />
      <SeoLocale />
      <Analytics />
      <FontPreload />
      <ScrollToHash />
      <Nav />

      {/* Стадия 2 i18n: все секции локализованы внутри себя (COPY.ru/COPY.en
          через useLocale) — /en и / рендерят один и тот же состав страницы. */}
      <main id="top">
        {/* Зона ДНК — вся страница, с первого экрана (редизайн 2026-09-20,
            продолжение мандата Тони 2026-08-03 «молекула держит весь разговор»).
            Hero и логотипы переехали ВНУТРЬ зоны: текст слева, молекула справа
            с первого пикселя — один мотив на всю страницу вместо трёх
            несвязанных визуалов (тор удалён, схема «одно окно» стала макетом).
            Прогресс облёта считается от высоты этой обёртки (SHOTS в DnaScene).
            Все секции — ПРЯМЫЕ дети обёртки: страж dna-layer считает именно их. */}
        <div className="relative dna-zone">
          <DnaLazy />
          <Hero />
          <ClientLogos />
          {/* «Проблема и кому» стоит вплотную перед продуктом: узнал себя →
              сразу ЧТО покупаешь, ЧЕМ доказано, СКОЛЬКО принесёт. Это же
              первые три пункта меню: Продукт · Кейсы · ROI-калькулятор
              (мандат 2026-08-03, страж section-order). */}
          <Audience />
          <TwoTracks />
          <Cases />
          <ValueCalculator />
          {/* Один макет «окно собственника» и раскрытие «как устроено внутри»
              вместо трёх текстовых секций (FullCoverage, Positioning, Process):
              продукт показываем, а не описываем. Якорь «Методология» живёт
              внутри — на полосе сроков. Отзывы — внутри блока кейсов. */}
          <HowItWorks />
          <WhyNotDiy />
          <Team />
          <Faq />
          <FinalCta />
          {/* Партнёрка стоит ПОСЛЕ формы: кто хотел купить — уже оставил
              заявку, а кто дочитал и не оставил, может привести того, кто купит. */}
          <Partners />
          <ReadNext />
        </div>
      </main>

      <Footer />
      <StickyCta />
    </>
  )
}
